import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  FaEye, FaEyeSlash, FaEnvelope, FaPhoneAlt, FaCommentDots, FaKey,
  FaSignInAlt, FaBriefcase, FaBuilding, FaUsers, FaBolt, FaShieldAlt, FaChartLine,
} from 'react-icons/fa';
import api from '../api/axios';
import GoogleSignInButton from '../pages/GoogleSignInButton';
import Navbar from './Navbar';
// 👉 Save the man + city-skyline cut-out (transparent PNG) here:
import loginHero from '../assets/login-hero.png';

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

const GRADIENT = 'bg-gradient-to-r from-[#6D3BF0] to-[#F0457A]';

function Login() {
  const [loginMode, setLoginMode] = useState('email'); // 'email' | 'phone'
  const [method, setMethod] = useState('password'); // 'password' | 'otp'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ---- OTP state ----
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpInputRefs = useRef([]);

  useEffect(() => {
    const prefillPhone = location.state?.prefillPhone;
    if (prefillPhone) {
      setLoginMode('phone');
      setPhone(prefillPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtp(Array(OTP_LENGTH).fill(''));
    setResendTimer(0);
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
  };

  const handleMethodChange = (m) => {
    setMethod(m);
    setError('');
    resetOtpFlow();
  };

  const routeByRole = (user) => {
    switch (user.role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'employer':
      case 'recruiter':
        navigate('/employer/jobs');
        break;
      case 'agent':
        navigate('/agent/dashboard');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setError('');

    if (loginMode === 'phone') {
      if (phone.length !== 10) {
        setError('Please enter a valid 10-digit phone number.');
        return;
      }
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setOtpSending(true);
    try {
      const availability = loginMode === 'phone'
        ? await api.post('/auth/check-availability', { phone })
        : await api.post('/auth/check-availability', { email });

      const exists = loginMode === 'phone' ? availability.data?.phone_exists : availability.data?.email_exists;
      if (!exists) {
        setOtpSending(false);
        if (loginMode === 'phone') {
          navigate('/register', { state: { prefillPhone: phone } });
        } else {
          navigate('/register');
        }
        return;
      }
    } catch (err) {
      console.warn('Availability check failed.', err.response?.data || err.message);
    }

    try {
      if (loginMode === 'phone') {
        await api.post('/otp/send', { phone });
      } else {
        await api.post('/otp/email/send', { email });
      }
      setOtpSent(true);
      setOtp(Array(OTP_LENGTH).fill(''));
      setResendTimer(RESEND_SECONDS);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || otpSending) return;
    await handleSendOtp();
  };

  const handleOtpDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < OTP_LENGTH - 1) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setOtp(next);
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    const code = otp.join('');
    if (code.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit code.`);
      return;
    }

    setOtpVerifying(true);
    try {
      const response = loginMode === 'phone'
        ? await api.post('/otp/login', { phone, otp: code })
        : await api.post('/otp/email/login', { email, otp: code });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      routeByRole(response.data.user);
    } catch (err) {
      if (err.response?.data?.incomplete_registration) {
        navigate('/register', {
          state: {
            resumePhone: err.response.data.phone,
            resumeOtpToken: err.response.data.otp_token,
            resumeStep: err.response.data.registration_step,
            resumeData: err.response.data.registration_data,
          },
        });
        return;
      }
      setError(err.response?.data?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (loginMode === 'phone' && phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setLoading(true);
    try {
      try {
        const availability = loginMode === 'phone'
          ? await api.post('/auth/check-availability', { phone })
          : await api.post('/auth/check-availability', { email });
        const exists = loginMode === 'phone' ? availability.data?.phone_exists : availability.data?.email_exists;
        if (!exists) {
          navigate('/register', loginMode === 'phone' ? { state: { prefillPhone: phone } } : undefined);
          return;
        }
      } catch (e) {
        // check failed -> fall through to normal login
      }

      const payload = loginMode === 'email'
        ? { email, password }
        : { phone: `+91${phone}`, password };

      const response = await api.post('/login', payload);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      routeByRole(response.data.user);
    } catch (err) {
      if (err.response?.data?.incomplete_registration) {
        navigate('/register', {
          state: {
            resumePhone: err.response.data.phone,
            resumeStep: err.response.data.registration_step,
            resumeData: err.response.data.registration_data,
          },
        });
        return;
      }
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setError('');
    try {
      const response = await api.post('/auth/google', { credential });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.needs_profile_completion) {
        navigate('/register', { state: { googleAuth: true, user: response.data.user } });
        return;
      }
      routeByRole(response.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    }
  };

  const inputBase =
    'w-full bg-[#F3F2FB] border border-[#ECEAF8] rounded-xl py-3 text-[15px] text-[#1E1B4B] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6D3BF0]/40 focus:border-transparent transition disabled:text-gray-500';

  const tabClass = (active) =>
    `flex-1 flex items-center justify-center gap-2.5 text-[15px] font-medium py-2.5 rounded-xl transition ${
      active ? `${GRADIENT} text-white shadow-md shadow-purple-500/30` : 'text-[#4B4A6A] hover:text-[#6D3BF0]'
    }`;

  const methodClass = (active) =>
    `flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-[15px] font-medium transition ${
      active
        ? `border-transparent ${GRADIENT} text-white shadow-md shadow-purple-500/30`
        : 'border-gray-200 bg-white text-[#3B3A5A] hover:border-[#6D3BF0]'
    }`;

  const primaryBtn =
    `w-full ${GRADIENT} text-white py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2.5 shadow-lg shadow-purple-500/25 hover:opacity-95 transition disabled:opacity-50`;

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      {/* handwriting font for the "Dream Big" tagline */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap');
        /* Shrink content on short screens (laptops, small browser windows) so nothing is cut off */
        @media (max-height: 860px) { .lj-zoom { zoom: .9; } }
        @media (max-height: 780px) { .lj-zoom { zoom: .82; } }
        @media (max-height: 700px) { .lj-zoom { zoom: .75; } }
        @media (max-height: 620px) { .lj-zoom { zoom: .66; } }
        @media (max-height: 560px) { .lj-zoom { zoom: .58; } }
      `}</style>

      <Navbar user={null} />

      <div className="relative flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden bg-gradient-to-br from-white via-[#F1F0FF] to-[#C9CBFF]">
        {/* decorative blobs */}
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#E4E3FF]/70 blur-3xl" />
        <div className="absolute top-1/4 right-[30%] w-[28rem] h-[34rem] rounded-full bg-[#B9BCFF]/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[34rem] h-72 rounded-[100%] bg-gradient-to-r from-[#7C5CF6] to-[#B9A8FF] opacity-80 blur-sm" />

        {/* ============ LEFT: hero ============ */}
        <div className="hidden lg:block relative flex-1 min-w-0">
          {/* Man + skyline */}
          <img
            src={loginHero}
            alt=""
            className="absolute bottom-0 right-0 h-[94%] max-w-none object-contain object-bottom pointer-events-none select-none"
            style={{
              WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 22%, #000 88%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 14%)',
              WebkitMaskComposite: 'source-in',
              maskImage: 'linear-gradient(to right, transparent 0, #000 22%, #000 88%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 14%)',
              maskComposite: 'intersect',
            }}
          />

          {/* Dream big tagline + plane */}
          <div className="absolute top-8 right-[8%] xl:right-[14%] z-10 -rotate-[8deg] text-[#1E2A78]" style={{ fontFamily: "'Caveat', cursive" }}>
            <p className="text-[34px] leading-[1.05] font-semibold">Dream Big...<br />Build Your Career...</p>
            <svg viewBox="0 0 160 14" className="w-44 -mt-1" fill="none">
              <path d="M2 11 C 50 2, 110 2, 158 6" stroke="#F58A1F" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <svg className="absolute top-[22%] right-[26%] xl:right-[30%] z-10 w-28" viewBox="0 0 120 110" fill="none" stroke="#3B2FCC" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round">
            <path d="M60 8 L110 24 L78 52 Z" fill="white" />
            <path d="M60 8 L70 34 L78 52" />
            <path d="M60 8 L38 38 L70 34" />
            <path d="M8 98 C 4 76, 26 66, 36 78 C 44 88, 28 98, 20 86 C 12 72, 40 56, 56 52" strokeDasharray="5 6" strokeWidth="2" />
          </svg>

          {/* Left copy */}
          <div className="relative z-10 h-full flex flex-col justify-center pl-[6%] xl:pl-[8%] pr-4 max-w-[640px]">
            <div className="lj-zoom">
            <span className="self-start inline-flex items-center gap-2.5 bg-white/80 border border-[#DAD8F5] text-[#1E1B4B] text-[15px] font-medium pl-2 pr-5 py-2 rounded-full mb-8 shadow-sm">
              <span className="w-6 h-6 rounded-full overflow-hidden flex flex-col border border-gray-200">
                <span className="flex-1 bg-[#FF9933]" />
                <span className="flex-1 bg-white flex items-center justify-center"><span className="w-1.5 h-1.5 rounded-full border border-[#000080]" /></span>
                <span className="flex-1 bg-[#138808]" />
              </span>
              India's Trusted Job Portal
            </span>

            <h1 className="text-5xl xl:text-[4.25rem] font-extrabold text-[#14123F] leading-[1.08] tracking-tight">
              Find the job<br />that finds{' '}
              <span className="relative inline-block text-[#6D3BF0]">
                you.
                <svg className="absolute left-[-6%] -bottom-2 w-[112%]" viewBox="0 0 200 14" fill="none" preserveAspectRatio="none">
                  <path d="M2 10 C 50 14, 140 12, 198 3" stroke="#F58A1F" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
              </span>
            </h1>

            <p className="mt-8 text-[#2B2A55] text-lg leading-snug max-w-md">
              Thousands of companies are hiring right now. Log in to pick up where you left off.
            </p>

            {/* Stats */}
            <div className="mt-8 flex bg-white rounded-2xl px-2 py-5 max-w-[30rem] shadow-lg shadow-[#3B1F70]/10">
              {[
                { icon: FaBriefcase, value: '10K+', label: 'Active Jobs', color: 'text-[#3B3FD6]' },
                { icon: FaBuilding, value: '5K+', label: 'Companies', color: 'text-[#3B3FD6]' },
                { icon: FaUsers, value: '50K+', label: 'Job Seekers', color: 'text-[#3B3FD6]' },
              ].map(({ icon: Icon, value, label, color }, i) => (
                <div key={label} className={`flex-1 text-center ${i > 0 ? 'border-l border-gray-200' : ''}`}>
                  <Icon className={`${color} text-2xl mx-auto mb-1.5`} />
                  <p className="text-2xl font-extrabold text-[#14123F] leading-none">{value}</p>
                  <p className="text-[#4B4A6A] text-sm mt-1">{label}</p>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="mt-7 flex gap-10 max-w-[30rem]">
              {[
                { icon: FaBolt, label: 'Fast & Easy Apply', bg: 'bg-[#FFF1E2]', color: 'text-[#F59E0B]' },
                { icon: FaShieldAlt, label: 'Secure & Reliable', bg: 'bg-[#E6E9FF]', color: 'text-[#3B4FD6]' },
                { icon: FaChartLine, label: 'Grow Your Career', bg: 'bg-[#EEE9FF]', color: 'text-[#7C3AED]' },
              ].map(({ icon: Icon, label, bg, color }) => (
                <div key={label} className="flex flex-col items-center text-center w-24">
                  <span className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center mb-2`}>
                    <Icon className={`${color} text-2xl`} />
                  </span>
                  <span className="text-[#2B2A55] text-[15px] leading-tight">{label}</span>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>

        {/* ============ RIGHT: login card ============ */}
        <div className="relative z-10 w-full lg:w-[36%] xl:w-[35%] lg:max-w-[38rem] flex-1 lg:flex-none min-h-0 overflow-y-auto flex justify-center px-4 sm:px-6 lg:px-6 xl:pr-10 py-5">
          <div className="lj-zoom w-full max-w-md lg:max-w-none my-auto bg-white rounded-3xl border border-white shadow-2xl shadow-indigo-500/15 px-6 sm:px-8 py-7">
            <h2 className="text-center text-3xl sm:text-[2.1rem] font-extrabold text-[#14123F] leading-tight">
              Welcome back! <span className="inline-block">👋</span>
            </h2>
            <p className="text-center text-[#4B4A6A] text-lg mt-1 mb-6">Login to continue your job search</p>

            <form
              onSubmit={method === 'password' ? handleSubmit : otpSent ? handleVerifyOtp : handleSendOtp}
            >
              {/* Mode tabs */}
              <div className="flex mb-5 bg-[#F3F2FB] rounded-2xl p-1 gap-1">
                <button type="button" className={tabClass(loginMode === 'email')}
                  onClick={() => { setLoginMode('email'); setError(''); resetOtpFlow(); }}>
                  <FaEnvelope size={18} /> Email
                </button>
                <button type="button" className={tabClass(loginMode === 'phone')}
                  onClick={() => { setLoginMode('phone'); setError(''); resetOtpFlow(); }}>
                  <FaPhoneAlt size={16} /> Phone
                </button>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-4 text-sm">{error}</div>
              )}

              {/* Identifier */}
              {loginMode === 'email' ? (
                <div className="mb-5">
                  <label htmlFor="login-email" className="block text-[17px] font-semibold mb-1.5 text-[#14123F]">Email</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6A8A]" size={18} />
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().replace(/\s/g, ''))}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      disabled={otpSent}
                      required
                      placeholder="you@example.com"
                      autoComplete="username"
                      inputMode="email"
                      className={`${inputBase} pl-12 pr-4`}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  {otpSent && (
                    <button type="button" onClick={resetOtpFlow} className="text-xs font-medium text-[#6D3BF0] hover:underline mt-1.5">
                      Change email
                    </button>
                  )}
                </div>
              ) : (
                <div className="mb-5">
                  <label htmlFor="login-phone" className="block text-[17px] font-semibold mb-1.5 text-[#14123F]">Phone number</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 border border-r-0 border-[#ECEAF8] rounded-l-xl bg-[#F3F2FB] text-[15px] text-[#4B4A6A]">
                      +91
                    </span>
                    <input
                      id="login-phone"
                      name="phone"
                      type="tel"
                      value={phone}
                      onChange={handlePhoneChange}
                      disabled={otpSent}
                      required
                      placeholder="98765 43210"
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={10}
                      className={`${inputBase} rounded-l-none px-4`}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  {otpSent && (
                    <button type="button" onClick={resetOtpFlow} className="text-xs font-medium text-[#6D3BF0] hover:underline mt-1.5">
                      Change number
                    </button>
                  )}
                </div>
              )}

              {/* Sign-in method */}
              <p className="text-[15px] text-[#3B3A5A] mb-2">How would you like to sign in?</p>
              <div className="flex gap-3 mb-5">
                <button type="button" className={methodClass(method === 'otp')} onClick={() => handleMethodChange('otp')}>
                  <FaCommentDots size={16} /> OTP
                </button>
                <button type="button" className={methodClass(method === 'password')} onClick={() => handleMethodChange('password')}>
                  <FaKey size={15} /> Password
                </button>
              </div>

              {method === 'password' ? (
                <>
                  <div className="mb-1.5">
                    <label htmlFor="login-password" className="block text-[17px] font-semibold mb-1.5 text-[#14123F]">Password</label>
                    <div className="relative">
                      <input
                        id="login-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="••••••••"
                        autoComplete="current-password"
                        className={`${inputBase} px-4 pr-12`}
                        style={{ fontSize: '16px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4B4A6A] hover:text-[#14123F]"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end mb-5">
                    <Link to="/forgot-password" className="text-sm font-medium text-[#6D3BF0] hover:underline">
                      Forgot Password?
                    </Link>
                  </div>

                  <button type="submit" disabled={loading} className={primaryBtn}>
                    <FaSignInAlt size={18} />
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                </>
              ) : otpSent ? (
                <>
                  <div className="mb-2">
                    <label className="block text-[17px] font-semibold mb-2 text-[#14123F]">
                      Enter {OTP_LENGTH}-digit code
                    </label>
                    <div className="flex gap-3 justify-between" onPaste={handleOtpPaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpInputRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpDigitChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          className="flex-1 min-w-0 h-14 text-center text-xl font-semibold bg-[#F3F2FB] border border-[#ECEAF8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6D3BF0]/40 transition"
                        />
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-3 mb-5">
                      <span className="text-xs text-[#4B4A6A]">
                        Sent to {loginMode === 'phone' ? `+91 ${phone}` : email}
                      </span>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendTimer > 0 || otpSending}
                        className="text-xs font-medium text-[#6D3BF0] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                      >
                        {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : otpSending ? 'Resending...' : 'Resend OTP'}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={otpVerifying} className={primaryBtn}>
                    <FaSignInAlt size={18} />
                    {otpVerifying ? 'Verifying...' : 'Verify & Login'}
                  </button>
                </>
              ) : (
                <button type="submit" disabled={otpSending} className={primaryBtn}>
                  <FaSignInAlt size={18} />
                  {otpSending ? 'Sending OTP...' : 'Send OTP'}
                </button>
              )}
            </form>

            {/* OR divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-[#14123F] font-medium">OR</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <GoogleSignInButton onCredential={handleGoogleCredential} onError={setError} />

            <p className="text-center text-[15px] mt-5 text-[#3B3A5A]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#6D3BF0] font-semibold hover:underline">
                Register Now
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;